import { type FC, useState } from "react";

import { faClockRotateLeft } from "@fortawesome/pro-duotone-svg-icons/faClockRotateLeft";
import { faEllipsis } from "@fortawesome/pro-duotone-svg-icons/faEllipsis";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Popover, PopoverButton } from "@headlessui/react";

import { type ConversationRefFragment } from "@app_schema";

import { Button } from "@styled/button";
import {
  ChatRow,
  ChatRowActions,
  ChatRowMeta,
  ChatRowName,
} from "@styled/chat_row";
import { DropdownPanel, DropdownPanelHeader } from "@styled/dropdown_panel";
import { Menu } from "@styled/menu";
import { MenuButton } from "@styled/menu_button";
import { MenuItemButton } from "@styled/menu_item_button";
import { MenuItems } from "@styled/menu_items";
import { PillChevron, PillLabel } from "@styled/pill_group";
import { RelativeDate } from "@styled/relative_date";
import { SearchInputField } from "@styled/search_input_field";

import { ChatRowRename } from "./chat_row_rename";

// Past about 8 chats, a search box.
const SEARCH_AFTER = 8;

// Previous chats: every chat on the case for this assistant, newest activity
// first, naming whoever started someone else's. The ⋯ holds Rename and Delete
// where you may use them.
export const PreviousChatsMenu: FC<{
  chats: ConversationRefFragment[];
  currentId?: string;
  onSelect(id: string): void;
  onRename(id: string, name: string): void;
  onDelete(chat: ConversationRefFragment): void;
}> = ({ chats, currentId, onSelect, onRename, onDelete }) => {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase();
  const shown = term
    ? chats.filter((chat) => chat.displayName.toLowerCase().includes(term))
    : chats;

  return (
    <Popover>
      <PopoverButton
        as={Button}
        type="button"
        color="white"
        size="sm"
        disabled={chats.length === 0}
      >
        <FontAwesomeIcon icon={faClockRotateLeft} />
        <PillLabel>Previous chats</PillLabel>
        <PillChevron />
      </PopoverButton>
      <DropdownPanel>
        {({ close }) => (
          <>
            {chats.length > SEARCH_AFTER && (
              <DropdownPanelHeader>
                <SearchInputField
                  query={search}
                  onSearch={(query) => setSearch(query ?? "")}
                  variant="sm"
                />
              </DropdownPanelHeader>
            )}
            {shown.map((chat) => (
              <ChatRow key={chat.id} current={chat.id === currentId}>
                {renamingId === chat.id ? (
                  <ChatRowRename
                    name={chat.displayName}
                    onSave={(name) => onRename(chat.id, name)}
                    onDone={() => setRenamingId(null)}
                  />
                ) : (
                  <>
                    <ChatRowName
                      current={chat.id === currentId}
                      onClick={() => {
                        onSelect(chat.id);
                        close();
                      }}
                    >
                      {chat.displayName}
                    </ChatRowName>
                    <ChatRowMeta>
                      {!chat.user.me && `${chat.user.name} · `}
                      <RelativeDate iso={chat.updatedAt} />
                    </ChatRowMeta>
                    {chat.policy.destroy && (
                      <ChatRowActions>
                        <Menu>
                          <MenuButton
                            color="white"
                            size="xs"
                            aria-label="Chat options"
                          >
                            <FontAwesomeIcon icon={faEllipsis} />
                          </MenuButton>
                          <MenuItems anchor="bottom end">
                            <MenuItemButton
                              size="sm"
                              onClick={() => setRenamingId(chat.id)}
                            >
                              Rename
                            </MenuItemButton>
                            <MenuItemButton
                              size="sm"
                              onClick={() => {
                                close();
                                onDelete(chat);
                              }}
                            >
                              Delete
                            </MenuItemButton>
                          </MenuItems>
                        </Menu>
                      </ChatRowActions>
                    )}
                  </>
                )}
              </ChatRow>
            ))}
          </>
        )}
      </DropdownPanel>
    </Popover>
  );
};
